import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
}

export default function HeroSection() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    let particles: Particle[] = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const particleCount = 70;
    const connectionDist = 150;

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        radius: Math.random() * 1.5 + 0.5,
      });
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
      });

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < connectionDist) {
            const opacity = 1 - dist / connectionDist;
            ctx.strokeStyle = `rgba(200, 170, 120, ${opacity * 0.25})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      particles.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(200, 170, 120, 0.6)';
        ctx.fill();
      });

      animId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-nv-black">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40" />

      <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 py-20 md:py-0">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col items-center text-center">
            <div className="inline-block px-4 py-2 rounded-full border border-white/30 mb-6 md:mb-8 animate-fade-in-up">
              <span className="text-white/80 text-xs md:text-sm tracking-wider uppercase">
                IT Solutions & Digital Growth
              </span>
            </div>
            <h1
              className="text-white text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-light leading-tight tracking-tight animate-fade-in-up"
              style={{ animationDelay: '0.2s' }}
            >
              Where Technology
              <br />
              <span className="font-normal italic">Meets Marketing</span>
            </h1>
            <div className="mt-8 md:mt-12 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              <a
                href="#our-services"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#our-services')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="inline-flex items-center gap-3 group"
              >
                <span className="text-white text-sm md:text-base font-medium">
                  Explore Our Services
                </span>
                <span className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-arrow-right-line text-nv-black text-lg" />
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}